import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { IApiResponseBody } from '../../../common/models/common';
import { ITelemetry } from '../../../services/master/telemetry/telemetry.model';
import { RootState } from '../../app.model';
import { deleteTelemetry, getTelemetryById, saveTelemetry, searchTelemetry } from './telemetry.action';
import { ITelemetryState } from './telemetry.model';
import { ISearchResponse } from '../../../common/models/common';

export const initialState: ITelemetryState = {
  search: {
    loading: false,
    hasErrors: false,
    data: [],
    count: 0,
    lookups: {},
    tableName: '',
  },
  tableColumnSelection: {
    id: null,
    table_name: null,
    columns: {},
  },
  getById: {
    loading: false,
    hasErrors: false,
    data: null,
  },
  save: {
    loading: false,
    hasErrors: false,
    messages: [],
  },
  delete: {
    loading: false,
    hasErrors: false,
    messages: [],
  },
};

export const telemetrySlice = createSlice({
  name: 'telemetry',
  initialState,
  reducers: {
    clearTelemetry: () => {
      return initialState;
    },
    clearTelemetryMessages: (state) => {
      state.save.messages = [];
      state.delete.messages = [];
    },
    clearTelemetryGetById: (state) => {
      state.getById.data = null;
    },
    setTableColumnSelection: (state, action: PayloadAction<{ [key: string]: boolean }>) => {
      state.tableColumnSelection.columns = action.payload;
    },
  },
  extraReducers: {
    // Search
    [searchTelemetry.pending.type]: (state) => {
      state.search.loading = true;
    },
    [searchTelemetry.fulfilled.type]: (state, action: PayloadAction<ISearchResponse<ITelemetry>>) => {
      const { search_result, ...rest } = action.payload;
      state.search.data = search_result.records;
      state.search.count = search_result.total_count;
      if (JSON.stringify(rest) !== '{}') {
        state.search.lookups = { ...rest };
      }
      state.search.loading = false;
      state.search.hasErrors = false;
      state.search.tableName = search_result.table_name;
      if (search_result.column_selection) {
        state.tableColumnSelection.id = search_result.column_selection.id;
        const tableSelectionObj = JSON.parse(search_result.column_selection.columns as any);
        if (tableSelectionObj.columns) {
          state.tableColumnSelection.column_orders = tableSelectionObj.column_orders;
          state.tableColumnSelection.columns = tableSelectionObj.columns;
        } else {
          state.tableColumnSelection.columns = tableSelectionObj;
        }
      }
      state.tableColumnSelection.table_name = search_result.table_name;
    },
    [searchTelemetry.rejected.type]: (state) => {
      state.search.loading = false;
      state.search.hasErrors = true;
    },

    // Get by id
    [getTelemetryById.pending.type]: (state) => {
      state.getById.loading = true;
    },
    [getTelemetryById.fulfilled.type]: (state, action: PayloadAction<ITelemetry>) => {
      state.getById.data = action.payload;
      state.getById.loading = false;
      state.getById.hasErrors = false;
    },
    [getTelemetryById.rejected.type]: (state) => {
      state.getById.loading = false;
      state.getById.hasErrors = true;
    },

    // Save
    [saveTelemetry.pending.type]: (state) => {
      state.save.loading = true;
      state.save.messages = [];
    },
    [saveTelemetry.fulfilled.type]: (state, action: PayloadAction<IApiResponseBody<unknown>>) => {
      state.save.loading = false;
      state.save.hasErrors = false;
      state.save.messages = action.payload.messages;
    },
    [saveTelemetry.rejected.type]: (state) => {
      state.save.loading = false;
      state.save.hasErrors = true;
    },

    // Delete
    [deleteTelemetry.pending.type]: (state) => {
      state.delete.loading = true;
      state.delete.messages = [];
    },
    [deleteTelemetry.fulfilled.type]: (state, action: PayloadAction<IApiResponseBody<unknown>>) => {
      state.delete.loading = false;
      state.delete.hasErrors = false;
      state.delete.messages = action.payload.messages;
    },
    [deleteTelemetry.rejected.type]: (state) => {
      state.delete.loading = false;
      state.delete.hasErrors = true;
    },
  },
});

// A selector
export const telemetrySelector = (state: RootState) => state.telemetry;

// Actions
export const { clearTelemetry, clearTelemetryMessages, clearTelemetryGetById, setTableColumnSelection } =
  telemetrySlice.actions;

// The reducer
export default telemetrySlice.reducer;
